import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelResearchRun, fetchResearchRun, openResearchRunStream } from '../atlasApi';

const TRACE_CAP = 500;
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
const TERMINAL_STATUSES = new Set(['complete', 'error', 'cancelled']);

// Per-run NDJSON stream consumer, modeled on the chat resume reader.
// Mount inside whatever renders the live view; unmounting aborts the stream.
// On mount it GETs the run record first: terminal runs render from result_json
// with no stream; active runs replay from last_id=0 (redis_id dedupe).
export default function useLiveResearch({ runId, enabled = true }) {
  const [run, setRun] = useState(null);
  const [status, setStatus] = useState('loading');
  const [connection, setConnection] = useState('idle');
  const [stepInfo, setStepInfo] = useState({ step: 0, maxSteps: null, url: null });
  const [liveUrl, setLiveUrl] = useState(null);
  const [latestFrame, setLatestFrame] = useState(null);
  const [trace, setTrace] = useState([]);
  const [findings, setFindings] = useState([]);
  const [captcha, setCaptcha] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const controllerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastRedisIdRef = useRef('0');
  const processedIdsRef = useRef(new Set());
  const traceIdRef = useRef(0);
  const frameSeqRef = useRef(0);
  const mountedRef = useRef(true);

  const appendTrace = useCallback((entry) => {
    traceIdRef.current += 1;
    const withId = { id: entry.redisId || `t${traceIdRef.current}`, ts: Date.now(), ...entry };
    setTrace((prev) => {
      const next = prev.length >= TRACE_CAP ? prev.slice(prev.length - TRACE_CAP + 1) : prev.slice();
      next.push(withId);
      return next;
    });
  }, []);

  const replaceFrame = useCallback((src) => {
    frameSeqRef.current += 1;
    setLatestFrame((prev) => {
      if (prev?.src && prev.src.startsWith('blob:')) {
        URL.revokeObjectURL(prev.src);
      }
      return { src, ts: Date.now() };
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!runId) return null;
    try {
      const record = await fetchResearchRun({ runId });
      if (!mountedRef.current) return record;
      setRun(record);
      if (record?.live_url) setLiveUrl((prev) => prev || record.live_url);
      if (record?.result) setResult(record.result);
      if (record?.status) setStatus(record.status);
      if (record?.error) setError(record.error);
      return record;
    } catch (e) {
      return null;
    }
  }, [runId]);

  const handleEvent = useCallback(
    (evt) => {
      if (!evt || typeof evt !== 'object') return;
      if (evt.redis_id) {
        if (processedIdsRef.current.has(evt.redis_id)) return;
        processedIdsRef.current.add(evt.redis_id);
        lastRedisIdRef.current = evt.redis_id;
      }
      const data = evt.data || {};
      switch (evt.type) {
        case 'run_status': {
          setStepInfo((prev) => ({
            step: data.step ?? prev.step,
            maxSteps: data.max_steps ?? prev.maxSteps,
            url: prev.url,
          }));
          if (data.name) {
            // The backend renames auto-named runs via the title subagent
            // shortly after start; pick it up without refetching.
            setRun((prev) => (prev && prev.name !== data.name ? { ...prev, name: data.name } : prev));
          }
          setStatus((prev) => {
            if (data.status && data.status !== prev) {
              appendTrace({ kind: 'status', text: `Status: ${data.status}`, redisId: evt.redis_id });
            }
            return data.status || prev;
          });
          break;
        }
        case 'thinking':
          appendTrace({ kind: 'thinking', text: data.text || '', redisId: evt.redis_id });
          break;
        case 'action':
          appendTrace({
            kind: 'action',
            text: data.kind || 'action',
            detail: data.detail,
            ok: data.ok !== false,
            message: data.message,
            redisId: evt.redis_id,
          });
          break;
        case 'screenshot': {
          if (data.image_b64) {
            replaceFrame(`data:image/jpeg;base64,${data.image_b64}`);
          }
          if (data.current_url) {
            setStepInfo((prev) => ({ ...prev, url: data.current_url }));
          }
          break;
        }
        case 'finding': {
          const finding = {
            index: data.index,
            label: data.label,
            note: data.note,
            url: data.url,
            screenshotFileId: data.screenshot_file_id || null,
          };
          setFindings((prev) => [...prev, finding]);
          appendTrace({ kind: 'finding', text: data.label || 'Finding', message: data.note, redisId: evt.redis_id });
          break;
        }
        case 'captcha': {
          if (data.state === 'found') {
            setCaptcha({ state: 'found', message: 'A verification challenge is being solved automatically…' });
          } else {
            setCaptcha(null);
          }
          appendTrace({ kind: 'captcha', text: `CAPTCHA ${data.state}`, redisId: evt.redis_id });
          break;
        }
        case 'live_url':
          if (data.live_url) setLiveUrl(data.live_url);
          break;
        case 'done': {
          setStatus('complete');
          setResult(data.result || null);
          setCaptcha(null);
          setConnection('closed');
          appendTrace({ kind: 'status', text: 'Run complete', redisId: evt.redis_id });
          break;
        }
        case 'error': {
          setStatus(data.status === 'cancelled' ? 'cancelled' : 'error');
          setError(data.error || 'Run failed');
          setCaptcha(null);
          setConnection('closed');
          appendTrace({ kind: 'error', text: data.error || 'Run failed', redisId: evt.redis_id });
          break;
        }
        case 'stream_incomplete': {
          setConnection('closed');
          break;
        }
        case 'keepalive':
        default:
          break;
      }
    },
    [appendTrace, replaceFrame]
  );

  const startStream = useCallback(
    async (fromId) => {
      if (!runId) return;
      const controller = new AbortController();
      controllerRef.current = controller;
      setConnection('connecting');
      let sawTerminal = false;
      try {
        const res = await openResearchRunStream({ runId, lastId: fromId, signal: controller.signal });
        setConnection('streaming');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            let evt;
            try {
              evt = JSON.parse(trimmed);
            } catch (e) {
              continue;
            }
            retryCountRef.current = 0;
            handleEvent(evt);
            if (evt.type === 'done' || evt.type === 'error' || evt.type === 'stream_incomplete') {
              sawTerminal = true;
            }
          }
        }
      } catch (e) {
        if (controller.signal.aborted) return;
      }
      if (controller.signal.aborted || !mountedRef.current) return;
      if (sawTerminal) {
        setConnection('closed');
        refresh();
        return;
      }
      // Stream ended without a terminal event: reconnect with backoff.
      const attempt = retryCountRef.current;
      if (attempt >= RECONNECT_DELAYS_MS.length) {
        setConnection('closed');
        refresh();
        return;
      }
      retryCountRef.current = attempt + 1;
      setConnection('reconnecting');
      retryTimerRef.current = setTimeout(() => {
        startStream(lastRedisIdRef.current);
      }, RECONNECT_DELAYS_MS[attempt]);
    },
    [runId, handleEvent, refresh]
  );

  useEffect(() => {
    mountedRef.current = true;
    // Reset all state for the new run.
    if (controllerRef.current) controllerRef.current.abort();
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryCountRef.current = 0;
    lastRedisIdRef.current = '0';
    processedIdsRef.current = new Set();
    setRun(null);
    setStatus('loading');
    setConnection('idle');
    setStepInfo({ step: 0, maxSteps: null, url: null });
    setLiveUrl(null);
    setLatestFrame(null);
    setTrace([]);
    setFindings([]);
    setCaptcha(null);
    setResult(null);
    setError(null);
    setCancelling(false);

    if (!runId || !enabled) return undefined;

    let cancelled = false;
    (async () => {
      const record = await refresh();
      if (cancelled || !mountedRef.current) return;
      const recordStatus = record?.status;
      if (recordStatus && TERMINAL_STATUSES.has(recordStatus)) {
        setConnection('closed');
        return;
      }
      startStream('0');
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (controllerRef.current) controllerRef.current.abort();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      setLatestFrame((prev) => {
        if (prev?.src && prev.src.startsWith('blob:')) URL.revokeObjectURL(prev.src);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, enabled]);

  const cancel = useCallback(async () => {
    if (!runId) return;
    setCancelling(true);
    try {
      await cancelResearchRun({ runId });
    } catch (e) {
      setCancelling(false);
    }
    // Terminal event arrives via the stream; cancelling flag clears on unmount/reset.
  }, [runId]);

  return {
    run,
    status,
    connection,
    stepInfo,
    liveUrl,
    latestFrame,
    trace,
    findings,
    captcha,
    result,
    error,
    cancelling,
    cancel,
    refresh,
  };
}
